import React, { CSSProperties, FC } from "react";
import { Link } from "react-router-dom";
import { useTranslate } from "../../utils/hooks/useTranslate";
import styles from "./text.module.scss";
import { clss } from "../../utils/helpers/styleHelpers";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/**
 * All text elements are wrappers for native elements but return translated values
 * */

type TextPropTypes = {
  className?: string;
  accessKey?: string;
  onClick?: (arg?: any) => void;
  id?: string;
  style?: CSSProperties;
  title?: string;
  tabIndex?: number;
  href?: string;
  target?: string;
  children?: any;
};

const H1: FC<TextPropTypes> = ({ children, className, ...props }) => {
  const { t } = useTranslate();

  const childrenString =
    typeof children === "object" ? (children as any).join("") : children;

  return (
    <h1
      {...props}
      className={clss([styles.h1, className])}
      dangerouslySetInnerHTML={{ __html: t(childrenString) }}
    ></h1>
  );
};

const H2: FC<TextPropTypes> = ({ children, className, ...props }) => {
  const { t } = useTranslate();

  const childrenString =
    typeof children === "object" ? (children as any).join("") : children;

  return (
    <h2
      {...props}
      className={clss([styles.h2, className])}
      dangerouslySetInnerHTML={{ __html: t(childrenString) }}
    ></h2>
  );
};

const H3: FC<TextPropTypes> = ({ children, className, ...props }) => {
  const { t } = useTranslate();

  const childrenString =
    typeof children === "object" ? (children as any).join("") : children;

  return (
    <h3
      {...props}
      className={clss([styles.h3, className])}
      dangerouslySetInnerHTML={{ __html: t(childrenString) }}
    ></h3>
  );
};

const H4: FC<TextPropTypes> = ({ children, className, ...props }) => {
  const { t } = useTranslate();

  const childrenString =
    typeof children === "object" ? (children as any).join("") : children;

  return (
    <h4
      {...props}
      className={clss([styles.h4, className])}
      dangerouslySetInnerHTML={{ __html: t(childrenString) }}
    ></h4>
  );
};

const H5: FC<TextPropTypes> = ({ children, className, ...props }) => {
  const { t } = useTranslate();

  const childrenString =
    typeof children === "object" ? (children as any).join("") : children;

  return (
    <h5
      {...props}
      className={clss([styles.h5, className])}
      dangerouslySetInnerHTML={{ __html: t(childrenString) }}
    ></h5>
  );
};

const H6: FC<TextPropTypes> = ({ children, className, ...props }) => {
  const { t } = useTranslate();

  const childrenString =
    typeof children === "object" ? (children as any).join("") : children;

  return (
    <h6
      {...props}
      className={clss([styles.h6, className])}
      dangerouslySetInnerHTML={{ __html: t(childrenString) }}
    ></h6>
  );
};

const Span: FC<TextPropTypes> = ({ children, className, ...props }) => {
  const { t } = useTranslate();

  const childrenString =
    typeof children === "object" ? (children as any).join("") : children;

  return (
    <span
      {...props}
      className={clss([styles.span, className])}
      dangerouslySetInnerHTML={{ __html: t(childrenString) }}
    ></span>
  );
};

const P: FC<TextPropTypes> = ({ children, className, ...props }) => {
  const { t } = useTranslate();

  const childrenString =
    typeof children === "object" ? (children as any).join("") : children;

  return (
    <p
      {...props}
      className={clss([styles.p, className])}
      dangerouslySetInnerHTML={{ __html: t(childrenString) }}
    ></p>
  );
};

const A: FC<TextPropTypes> = ({
  children,
  className,
  href,
  target = "_blank",
  ...props
}) => {
  const { t } = useTranslate();

  const childrenString =
    typeof children === "object" ? (children as any).join("") : children;

  return (
    <a
      {...props}
      className={clss([styles.a, className])}
      href={href}
      target={target}
      dangerouslySetInnerHTML={{ __html: t(childrenString) }}
    ></a>
  );
};

/**
 * Component for rendering markdown content with translation support
 * Use this when your translation contains markdown syntax
 * Supports tables, strikethrough, task lists, and other GitHub Flavored Markdown features
 */
type MarkdownPPropTypes = Omit<TextPropTypes, "href" | "target">;

const MarkdownP: FC<MarkdownPPropTypes> = ({ children, className, ...props }) => {
  const { t } = useTranslate();

  const childrenString =
    typeof children === "object" ? (children as any).join("") : children;

  const translatedText = t(childrenString);

  return (
    <div
      {...props}
      className={clss([styles.p, className])}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Internal links (e.g. /terms, /privacy-policy) navigate via the router (no full reload);
          // external links open safely in a new tab.
          a: ({ href, children: linkChildren, node, ...rest }) => {
            const url = href ?? "";
            if (url.startsWith("/")) {
              return <Link to={url}>{linkChildren}</Link>;
            }
            return (
              <a href={url} target="_blank" rel="noopener noreferrer" {...rest}>
                {linkChildren}
              </a>
            );
          },
        }}
      >
        {translatedText}
      </ReactMarkdown>
    </div>
  );
};

export { H1, H2, H3, H4, H5, H6, Span, P, A, MarkdownP };
